/**
 * @author pkleim on 3/31/16
 *
 * @fileOverview Renders a Login Component
 * @module Login
 * @requires LoginLinks
 * @requires LoginMessage
 * @see <a href="https://enterprise-confluence.aexp.com/confluence/display/DCE/Login+Component+User+Guide">Login Component User's Guide</a>
 * @param {string} destPage - Optional. Destination page on successful login.
 * @param {string} locale - Required. desired locale, e.g. en-US.
 * @param {object} language - Required. language object associated with locale;
 * @param {string} userId - Optional. initial value of userId field
 * @param {string} password - Optional. initial value of password field
 * @param {boolean} hideLinks - Default false. should the bottom links be hidden.
 * @param {boolean} hideRemember - Default false. Hide the Remember Me checkbox
 * @param {boolean} showSuccess - Default false. Show message on successful login.
 * @param {boolean} loadingProgress - Default false. should progress indicator be displayed.
 * @param {function} onSuccess - Optional. Function to be called on MYCA login success
 * @param {function} onClickSecLinks - Optional. Callback function to be called for secondary links.
 * Accepts login API response object
 * @param {function} onFailure - Optional. Function to be called on MYCA login failure
 * Accepts login API response object
 * @param {boolean} shouldFocus - Default true. Enables autofocus to the login input field.

 *
 * @example
 * import Login from 'one-identity-login';
 * <Login hideMenu />
 *
 */
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';

import React, { Component } from 'react';
import { func, oneOf } from 'prop-types';
import { compose } from 'redux';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { connect } from 'react-redux';
import {
  legacyLogin,
  sessionLogin,
  ssoLogin,
  updateAuthState,
  procedures,
} from '@americanexpress/one-identity-ducks';
import { withRouter } from '@americanexpress/one-app-router';
import { OnVisible } from '@americanexpress/data-manager-utils';
import {
  AgentBindingProvider,
  AgentBindingProviderContext,
} from '@americanexpress/one-identity-components/lib/components/AgentBindingProvider';
import LoginUI from './LoginUI';
import { setAppLocation } from '../utils';
import {
  LOGIN_CLEAR_STATUS,
  LOGIN_SET_DESTINATION,
  LOGIN_SET_LOCATION,
  LOGIN_SET_CONTEXT,
  LOGIN_SET_USERID,
  reducer,
  LOGIN_SET_HAS_DEVICE_REMEMBERED,
} from '../duck';
import {
  getInauthScriptDomain,
  getLoginLinks,
  getLoginMenu,
  getSSOLoginV2,
  getSSOLoginV4,
  getDestination,
  getAuthLoginUrl,
  getAuthLoginStatusCode,
  getAuthLoginErrorCode,
  getUserLinks,
  getEffectiveKey,
  getNormalizedQuery,
  getDefaultDestination,
  getLogSuccess,
  getCorrelationId,
  getRecaptchKey,
} from '../selectors';

import { MODULE_NAME } from '../scripts/constants';
import SetLocalStorageCookie from './SetLocalStorageCookie';

export const STAGE_LOGIN = 0;
export const STAGE_AUTHN = 1;

function hideLoginLogoutButton() {
  return {
    type: 'modules/header/HIDE_LOGIN_LOGOUT_BUTTON',
  };
}

function showLoginLogoutButton() {
  return {
    type: 'modules/header/SHOW_LOGIN_LOGOUT_BUTTON',
  };
}

export class Login extends Component {
  componentWillUnmount() {
    const { updateAuth } = this.props;
    updateAuth({
      status: undefined,
    });
  }

  render() {
    const { moduleLoadStatus, changeUserProfile } = this.props;

    if (moduleLoadStatus === 'loading') {
      return null;
    }

    return (
      <OnVisible analyticsInfo={{ element: 'Login' }}>
        <div id="LoginComponent">
          <SetLocalStorageCookie changeUserProfile={changeUserProfile} />
          <AgentBindingProvider>
            <AgentBindingProviderContext.Consumer>
              {({ agentBindingData }) => (
                <LoginUI
                  agentBindingData={agentBindingData}
                  // eslint-disable-next-line react/jsx-props-no-spreading -- allowing prop spreading
                  {...this.props}
                />
              )}
            </AgentBindingProviderContext.Consumer>
          </AgentBindingProvider>
        </div>
      </OnVisible>
    );
  }
}

Login.propTypes = {
  moduleLoadStatus: oneOf(['loading', 'loaded', 'error']),
  updateAuth: func.isRequired,
  changeUserProfile: func.isRequired,
};

export const mapStateToProps = (state, ownProps) => {
  const userId = ownProps.userId || '';
  const {
    router: {
      location: { query, search, pathname },
    },
  } = ownProps;

  return {
    config: state.getIn(['config']),
    locale: state.getIn(['intl', 'activeLocale']),
    statusCode: getAuthLoginStatusCode(state),
    errorCode: getAuthLoginErrorCode(state),
    loginProgress: state.getIn(['modules', 'one-identity-root', 'auth', 'loginLoading']),
    location: state.getIn(['router', 'location']),
    loginUsApiDomain: state.getIn(['config', 'loginUsApiDomain']),
    query: getNormalizedQuery(query),
    pathname,
    search,
    destinationFromState: getDestination(state),
    loginUrl: getAuthLoginUrl(state),
    links: getLoginLinks(ownProps, state),
    menu: getLoginMenu(ownProps, state),
    ssoLoginV2: getSSOLoginV2(ownProps),
    ssoLoginV4: getSSOLoginV4(ownProps),
    inauthScriptDomain: getInauthScriptDomain(state),
    defaultDestination: getDefaultDestination(ownProps, state),
    userLinks: getUserLinks(ownProps),
    effectiveKey: getEffectiveKey(ownProps, state),
    userId,
    type: ownProps.type,
    faasApiUrl: state.getIn(['config', 'faasApiUrl']),
    isLogSuccess: getLogSuccess(state),
    correlationId: getCorrelationId(state),
    recaptchaSiteKey: getRecaptchKey(state),
  };
};

// use dispatch when it is required...
export const mapDispatchToProps = (dispatch, { router: { replace, push } }) => {
  const { getUserNameByUserId } = procedures;
  const props = {
    sessionLogin: (...args) => dispatch(sessionLogin(...args)),
    ssoLogin: (...args) => {
      dispatch(ssoLogin(...args));
    },
    legacyLogin: (...args) => {
      dispatch(legacyLogin(...args));
    },
    getUserNameByUserId: (...args) => dispatch(getUserNameByUserId(...args)),
    hideLoginLogoutButton: () => {
      dispatch(hideLoginLogoutButton());
    },
    showLoginLogoutButton: () => {
      dispatch(showLoginLogoutButton());
    },
    setAppLocation: (location) => {
      setAppLocation(location, replace);
    },
    clearStatus: () => {
      dispatch({ type: LOGIN_CLEAR_STATUS });
    },
    changeDestination: (destination) => {
      dispatch({
        type: LOGIN_SET_DESTINATION,
        destination,
      });
    },
    updateAuth: (...args) => {
      dispatch(updateAuthState(...args));
    },
    push: (...args) => {
      dispatch(push(...args));
    },
    setLocation: () => {
      dispatch({ type: LOGIN_SET_LOCATION });
    },
    setContext: (context) => {
      dispatch({ type: LOGIN_SET_CONTEXT, context });
    },
    changeUserProfile: (userId) => {
      dispatch({ type: LOGIN_SET_USERID, userId });
    },
    setHasDeviceRemembered: () => {
      dispatch({ type: LOGIN_SET_HAS_DEVICE_REMEMBERED, hasDeviceRemembered: true });
    },
  };

  return props;
};

export const loadModuleData = ({ store: { dispatch } }) => {
  dispatch(loadLanguagePack(MODULE_NAME, { fallbackLocale: 'en-US' }));
};

Login.holocron = {
  name: 'one-identity-login',
  reducer,
  loadModuleData,
};

const hocChain = compose(
  withRouter,
  oneAppModuleWrapper(MODULE_NAME),
  connect(mapStateToProps, mapDispatchToProps)
);

export default hocChain(Login);
